import {
  createReadonlyObservable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';

import { Counterparty } from '#/unified-rfqs/entities/drfq/domain';

type DesksList = readonly Counterparty['desk_name'][];

export interface CpsSearch {
  readonly teardown: () => void;
  readonly setDebounced: (value: string) => void;
  /**
   * We don't observe the stored CPs because they change on updates like
   * selection/favorite and search is pretty expensive as we usually
   * have thousands of options to loop through.
   *
   * This should be called preferably only once as the data from the
   * server is fetched.
   */
  readonly configure: (data: readonly Counterparty[]) => void;
  /**
   * Search results as a list of desk names.
   *
   * This will only contain desks that satisfies the search term and that
   * are listed by `orderedDesks`, it'll follow the same order as in
   * `orderedDesks`.
   */
  readonly results: ReadonlyObservable<DesksList>;
}

interface CpsSearchParams {
  readonly orderedDesks: ReadonlyObservable<DesksList>;
  readonly debounceMs?: number;
}

export default function createCpsSearch(params: CpsSearchParams): CpsSearch {
  const { orderedDesks, debounceMs = 200 } = params;

  const [results, setResults] = createReadonlyObservable<DesksList>(
    'cps-search-results',
    [],
  );

  let input = '';
  let timeout: NodeJS.Timeout | undefined;
  let searchData: readonly Counterparty[] = [];
  let desksSatisfyingSearch: ReadonlySet<string> = new Set();

  orderedDesks.observe(updateResults);

  function teardown() {
    clearTimeout(timeout);
    orderedDesks.unobserve(updateResults);
  }

  function updateResults(desks: DesksList) {
    const filteredDesks = desks.filter(
      desksSatisfyingSearch.has.bind(desksSatisfyingSearch),
    );
    setResults(filteredDesks);
  }

  function updateSearch() {
    const filteredCps =
      input.length !== 0
        ? searchData.filter(
            (cp) =>
              cp.desk_name.toUpperCase().includes(input) ||
              cp.firm_name.toUpperCase().includes(input),
          )
        : searchData;

    desksSatisfyingSearch = new Set(filteredCps.map((cp) => cp.desk_name));
    updateResults(orderedDesks.get());
  }

  function setDebounced(value: string) {
    clearTimeout(timeout);

    value = value.trim().toUpperCase();
    if (input === value) return;

    timeout = setTimeout(() => {
      timeout = undefined;
      input = value;
      updateSearch();
    }, debounceMs);
  }

  function configure(data: readonly Counterparty[]) {
    if (searchData === data) return;
    searchData = data;
    updateSearch();
  }

  return {
    teardown,
    setDebounced,
    configure,
    results,
  };
}
